/**
 * One lazily-loaded surface, with the wait said in the house voice.
 *
 * Every surface is its own chunk (`studio/06`), so a parent opening the Library does not
 * also download the composer, the picker and the shelf. The cost is a moment between the
 * click and the surface, and the design has a word for that moment: it is not a spinner and
 * not a skeleton pretending to be content — it is the surface header's kicker, alone, saying
 * what is opening. On a warm cache it is never seen at all.
 *
 * `lazySurface` adds one thing `React.lazy` does not have: a **`preload`**. Once a surface's
 * module is in hand it is rendered directly, with no boundary to suspend at — which is what
 * lets a caller warm a surface ahead of the click, and what lets the tests mount a route
 * synchronously against the real route table instead of a second, eager copy of it.
 */

import { Suspense, lazy } from 'react';
import type { ComponentType, JSX } from 'react';

import { useT } from '../i18n/I18nProvider.js';

function Opening(): JSX.Element {
  const t = useT();
  return (
    <div className="surfhead">
      <div>
        <span className="kicker">{t('surface.opening')}</span>
      </div>
    </div>
  );
}

export interface LazySurface {
  (): JSX.Element;
  /** Fetch the chunk now. Idempotent — the module cache does the deduplicating. */
  preload: () => Promise<void>;
}

export function lazySurface(load: () => Promise<{ default: ComponentType }>): LazySurface {
  let Loaded: ComponentType | null = null;
  const Lazy = lazy(async () => {
    const module = await load();
    Loaded = module.default;
    return module;
  });

  const Surface: LazySurface = () => {
    const Ready = Loaded;
    return Ready === null ? <Lazy /> : <Ready />;
  };
  Surface.preload = async () => {
    Loaded = (await load()).default;
  };
  return Surface;
}

/** Wrap a lazy surface so the route table stays a table. */
export function surface(Component: ComponentType): JSX.Element {
  return (
    <Suspense fallback={<Opening />}>
      <Component />
    </Suspense>
  );
}
